import { TMicroDefinition, IMicroConfiguration, IMicroInterlink, TMicroPlugin } from '../types/configuration';
import { ResourceLoader } from '../builders/resource_loader';
import { MicroBuilder } from '../builders/micro_builder';
import { ViewModelProcess } from './view_model';
import { RoutingProcess } from './routing';
import { Micro } from '../core/micro';

import { Process } from'../../../pip/library/pi-interop/pi-interop.js';
import $ip from'../../../pip/library/pi-ip/pi-ip.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';

export interface IExternalMicroRequest {
  configuration: IMicroInterlink;
  micro: Micro<IMicroConfiguration>;
}

export class MicroModelProcess extends Process {
  id = 'micro_model';

  configuration: IMicroConfiguration;

  dependencies = ['/view_model', '/routing'];
  processes: {
    view_model: ViewModelProcess;
    routing: RoutingProcess;
  };

  models: {
    microTable: Record<string, IExternalMicroRequest>;
  };

  initialize(configuration: IMicroConfiguration) {
    const externals = f1.read(f1.lens('/routes/externals'), [], configuration);

    this.configuration = configuration;
    this.models = {
      microTable: {}
    };

    return f1
      .parallel(
        f1.map((microInterlink: IMicroInterlink) => {
          microInterlink = f1.merge(
            'over',
            {
              location_uri: '',
              resource_uri: '',
              source_uri: ''
            },
            microInterlink
          );

          return () =>
            f1.tasks(
              [
                (externalMicro) => {
                  // 1. load micro remotely
                  return ResourceLoader.loadMicro(externalMicro);
                },
                (MicroJS: TMicroDefinition) => {
                  // 2. create the micro
                  return MicroBuilder.build(MicroJS);
                },
                (micro: Micro<IMicroConfiguration>) => {
                  // 3. augment the parent router to include the micro
                  const microSourceURI = microInterlink.source_uri;
                  const microLocationURI = microInterlink.location_uri;

                  const topRoutesLens = f1.lens('/routes/routes');
                  f1.write(
                    topRoutesLens,
                    f1.push(
                      {
                        uri: `${microLocationURI}/*`,
                        regions: [
                          {
                            // @todo the micro region should default to main
                            region: 'main',
                            view_preloaded: microSourceURI
                          }
                        ]
                      },
                      f1.read(topRoutesLens, [], configuration)
                    ),
                    configuration
                  );

                  return f1.done(micro);
                },
                (micro: Micro<IMicroConfiguration>) => {
                  // 4. configure the interlink parameters
                  return f1.done(
                    micro.interlinkConfiguration({
                      ...microInterlink,
                      location_uri: `${this.configuration.routes.uri}${microInterlink.location_uri}`
                    })
                  );
                },
                (micro: Micro<IMicroConfiguration>) => {
                  // 5. configure the interlink parameters
                  return $ip.requires<TMicroPlugin>(f1.read('/import/plugins', [], microInterlink)).then((plugins) => {
                    micro.plugins = plugins;
                    return micro;
                  });
                },
                (micro: Micro<IMicroConfiguration>) => {
                  // 6. save the micro
                  this.models.microTable[micro.id] = {
                    configuration: microInterlink,
                    micro
                  };

                  return f1.done(micro);
                }
              ],
              microInterlink
            );
        }, externals),
        f1.skip
      )
      .then(() => this);
  }

  activate() {
    return f1
      .parallel(
        f1.map((microEntry) => {
          return () =>
            f1.tasks(
              [
                (microEntry: IExternalMicroRequest) => {
                  // 2. get the $delegate view of the micro and add it to a new preloaded_view list
                  const micro = microEntry.micro;
                  const view = micro.$view();
                  const microSourceURI = microEntry.configuration.source_uri;
                  const viewModelProcess = this.processes.view_model;

                  viewModelProcess.models.preloadedViews[microSourceURI] = view;
                  return f1.done(microEntry);
                }
              ],
              microEntry
            );
        }, f1.to.array(this.models.microTable)),
        f1.skip
      )
      .then(() => this);
  }
}
